import LegalPageLayout from '../components/LegalPageLayout';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <div className="space-y-4 text-sm leading-7 text-gray-600">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="This draft policy explains what Filie collects, how we use it, and the choices available to beta users."
      lastUpdated="April 13, 2026"
    >
      <Section title="Overview">
        <p>
          Filie Inc. ("Filie," "we," "our," or "us") provides a federal tax analysis and document review product.
          This Privacy Policy applies to the Filie website, applications, and related services (the "Service").
        </p>
        <p>
          This is a working beta policy and should be reviewed by counsel before broad public launch. It is intended
          to describe Filie&apos;s current practices as of April 13, 2026.
        </p>
      </Section>

      <Section title="Information We Collect">
        <p>We may collect the following categories of information:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Account information such as your name, email address, login credentials, and invite code usage.</li>
          <li>Tax documents and related uploads, including filenames, file metadata, and document classifications.</li>
          <li>Questionnaire responses, tax profile data, and values you review or correct in the product.</li>
          <li>Generated outputs such as tax summaries, form drafts, and deterministic tax computation results.</li>
          <li>Usage and diagnostic data such as page activity, upload and analysis events, device/browser metadata, and error reports.</li>
          <li>Feedback, support requests, and issue reports you submit during beta.</li>
        </ul>
      </Section>

      <Section title="How We Collect Information">
        <p>We collect information directly from you when you create an account, upload documents, complete the questionnaire, or contact us.</p>
        <p>
          We also collect system-generated data from your use of the Service, including activity logs, authentication
          events, and application error telemetry.
        </p>
      </Section>

      <Section title="How We Use Information">
        <ul className="list-disc space-y-2 pl-6">
          <li>Provide, maintain, and improve the Service.</li>
          <li>Process uploaded documents and generate federal tax analysis outputs.</li>
          <li>Authenticate users, secure accounts, detect abuse, and investigate suspicious activity.</li>
          <li>Monitor reliability, troubleshoot bugs, and measure product performance during beta.</li>
          <li>Communicate with you about access, support, important product notices, and policy changes.</li>
          <li>Comply with legal obligations and enforce our terms.</li>
        </ul>
      </Section>

      <Section title="AI Processing and Sensitive Data">
        <p>
          Filie uses third-party AI services to help classify documents and extract tax-relevant fields. Our current
          production pipeline is designed so that Social Security numbers and EINs are extracted locally and redacted
          from document images before those images are sent to an external AI provider for analysis.
        </p>
        <p>
          Filie&apos;s deterministic engine, not the AI provider, performs tax calculations such as AGI, taxable income,
          credits, and refund or amount owed. You should still review extracted values before relying on any result.
        </p>
      </Section>

      <Section title="How We Share Information">
        <p>We may share information with:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Infrastructure and storage providers that host the Service or store uploaded documents.</li>
          <li>AI and analytics providers that help us analyze documents, monitor product performance, and diagnose errors.</li>
          <li>Professional advisors, auditors, insurers, or acquirers in connection with business operations or transactions.</li>
          <li>Law enforcement, regulators, or other third parties when required by law or necessary to protect rights, safety, or the Service.</li>
        </ul>
        <p>
          We do not intentionally sell personal information. If that changes, we will update this policy before doing so.
        </p>
      </Section>

      <Section title="Retention">
        <p>
          We retain information for as long as reasonably necessary to provide the Service, operate the beta, comply
          with legal obligations, resolve disputes, and enforce our agreements. Because Filie is in beta, retention
          periods may evolve as the product matures.
        </p>
      </Section>

      <Section title="Security">
        <p>
          We use administrative, technical, and organizational measures intended to protect personal information,
          including authentication controls, document storage protections, encryption in transit, and production
          monitoring. No system is perfectly secure, and we cannot guarantee absolute security.
        </p>
      </Section>

      <Section title="Your Choices and Rights">
        <p>
          You can access, correct, or delete certain information through your account settings or by contacting us.
          Beta users may also request account deletion and data export through the application or by email.
        </p>
        <p>
          If you are a California resident, you may have rights under the California Consumer Privacy Act and related
          regulations, including rights to know, delete, correct, and limit certain uses of personal information,
          subject to applicable exceptions.
        </p>
      </Section>

      <Section title="Children">
        <p>
          The Service is not directed to children under 13, and we do not knowingly collect personal information from
          children under 13.
        </p>
      </Section>

      <Section title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will post the updated
          version here and revise the last updated date above.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions, privacy requests, or legal notices can be sent to
          {' '}<a className="text-gray-900 underline" href="mailto:info@filie.ai">info@filie.ai</a>.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
